// utils/trackingRequestUtils.tsx

import React from 'react';
import _ from 'lodash';
import { ApiResult } from '../framework.core/data/ApiResult';
import { RequestType, TrackRequest } from '../fms.core/TrackRequest';
import constants from '../constants';

export interface TrackingRequestModalState {
  jobId: number;
  trStatus: string;
  trType: string;
  containerNo: string;
  mode: 'new' | 'view';
  vcResult: string;
  prefetchedGroupData?: TrackRequest[];
}

type ShowDialogFn = (
  content: React.ReactNode,
  hideOkButton?: boolean,
  top?: string | null,
  type?: 'info' | 'error' | 'warning'
) => Promise<void>;

/**
 * 선사 지원 여부 확인
 */
export async function checkShippingLineSupport(
  api: any,
  vcScac: string,
  reqType: string,
  showDialog: ShowDialogFn
): Promise<ApiResult<string>> {
  let apiResult: ApiResult<string> = { success: false, result: '', message: '' };

  if (_.isEmpty(vcScac)) {
    await showDialog(
      <p>Vessel carrier was not given. Please retry with valid Master B/L number.</p>,
      false, null, 'warning'
    );
    return apiResult;
  }

  apiResult = await api.trackingCheckShippingLine(vcScac);

  if (!apiResult || !apiResult.success) {
    await showDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
    return apiResult;
  }

  switch (apiResult.result) {
    case 'X':
      await showDialog(
        <p>Tracking Service does not support Vessel carrier {`[${vcScac}]`}.</p>,
        false, null, 'warning'
      );
      apiResult.success = false;
      break;
    case RequestType.bl:
      if (reqType !== RequestType.bl) {
        await showDialog(
          <p>Vessel carrier {`[${vcScac}] support only Master B/L tracking.`}
          Please retry from Master B/L list.</p>,
          false, null, 'warning'
        );
        apiResult.success = false;
      }
      break;
  }

  return apiResult;
}

/**
 * 트래킹 상태 확인 - view 모드인지
 */
export function isViewMode(trType: string, trStatus: string): boolean {
  return !_.isEmpty(trType) && trType !== 'C' && trStatus !== 'failed';
}

/**
 * 트래킹 요청 모달 제목
 */
export function getTrackRequestTitle(trStatus: string): string {
  return 'Tracking Request' +
    (!_.isEmpty(trStatus) && trStatus !== 'Q'
      ? ` - ${trStatus.toUpperCase().replace('_', ' ')}`
      : '');
}

/**
 * 이메일 옵션 모달 제목
 */
export function getEmailOptionTitle(mblNo: string): string {
  return `[MBL #${mblNo}] Tracking Request: Choose email notice recipients`;
}

/**
 * 트래킹 상태 정보 포맷팅
 */
export function formatTrackingStatusInfo(
  trType: string,
  trStatus: string,
  trDate?: string | null,
  trStatusDesc?: string,
  lastEventDate?: string | null,
  lastEvent?: string
): { date: string | null; statusDesc: string } {
  let date: string | null = null;
  let statusDesc = '';

  if (trType === 'M' || trType === 'C') {
    switch (trStatus) {
      case 'Q':
        date = trDate ?? null;
        statusDesc = 'Start to Request';
        break;
      case 'failed':
        date = trDate ?? null;
        statusDesc = trStatusDesc || '';
        break;
      case 'tracking_stopped':
        date = lastEventDate ?? null;
        statusDesc = lastEvent || '';
        break;
      case 'pending':
        date = lastEventDate ?? null;
        statusDesc = 'Pending';
        break;
      case 'awaiting_manifest':
        date = lastEventDate ?? null;
        statusDesc = 'Awaiting Manifest';
        break;
      default:
        date = lastEventDate ?? null;
        statusDesc = lastEvent || '';
    }
  }

  // 상태 설명 포맷팅
  if (statusDesc) {
    if (statusDesc.includes('eta')) statusDesc = statusDesc.replaceAll('eta', 'ETA');
    else if (statusDesc.includes('lfd')) statusDesc = statusDesc.replaceAll('lfd', 'LFD');
    else if (statusDesc.includes('full')) statusDesc = statusDesc.replaceAll('full', 'Gate');
    else if (statusDesc.includes('pod')) statusDesc = statusDesc.replaceAll('pod', 'POD');
  }

  return { date, statusDesc };
}